import { Tooltip } from '@/components';
import { Card } from '@/components/Card';
import { Container } from '@/components/Container';
import { LabelCard } from '@/components/LabelCard/LabelCard';
import { PageDetailsHeaderRow } from '@/components/PageDetailsHeaderRow/PageDetailsHeaderRow';
import { StatusCard } from '@/components/StatusCard/StatusCard';
import { ProcessDetails } from '@/modules/Processes/domain';
import {
  formatNumber,
  getFullString,
  secondsToTimeDurationString,
} from '@/utils';
import { SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditProcessModal } from '../../../ProcessesPage/components';

interface HeaderRowProps {
  process?: ProcessDetails;
  handleStatus: (
    checked: boolean,
    event: MouseEvent | SyntheticEvent<MouseEvent | KeyboardEvent>,
    id: string,
  ) => void;
  status: boolean;
  isLoading: boolean;
}

export const ProcessHeaderRow = ({
  process,
  handleStatus,
  status,
}: HeaderRowProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const handleModalState = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  return (
    <div>
      <PageDetailsHeaderRow
        isLoading={!process}
        title={process?.name ?? ''}
        description={
          <div className="text-sm">
            <span className="font-bold" data-testid="org-comp-section">
              {`${process?.organization}, ${process?.company}, `}
            </span>
            <span data-testid="ba-section">{process?.businessAreaName}</span>
          </div>
        }
        actions={
          <>
            <LabelCard
              label={t('owner')}
              value={getFullString(
                process?.owner?.firstName ?? '',
                process?.owner.lastName ?? '',
              )}
            />
            <StatusCard status={status} handleStatus={handleStatus} />
          </>
        }
        onEditClick={handleModalState}
        details={
          <div className="flex flex-col gap-1">
            <Container className="flex-row">
              <Card title={t('process_description')}>
                <p data-testid="description-section">{process?.description}</p>
              </Card>
              <Card title={t('code')}>
                <p data-testid="code-section">{process?.code}</p>
              </Card>
            </Container>
            <Container className="flex-row">
              <Card title={t('total_users')}>
                <p data-testid="total-users-section">{process?.totalUsers}</p>
              </Card>
              <Card title={t('workload_monthly')}>
                <p data-testid="workload-section">
                  {secondsToTimeDurationString(
                    (process?.totalWorkload ?? 0) * 60,
                  )}
                </p>
              </Card>
              <Card title={t('estimated_cost_monthly')}>
                <Tooltip
                  containerClassName="w-fit"
                  showArrow
                  content={`${formatNumber(process?.totalEstimatedCost ?? 0, 4)}€`}
                >
                  {formatNumber(process?.totalEstimatedCost ?? 0)}€
                </Tooltip>
              </Card>
            </Container>
          </div>
        }
      />
      {isOpen && process?.id && (
        <EditProcessModal
          isOpen={isOpen}
          id={process.id}
          handleClose={handleModalState}
        />
      )}
    </div>
  );
};
